/**
 * NavDrawer — the navigation below 1024 px (design/ux/01_foundations.md §6.4
 * « Drawer », §5.1; D-21; AUD-NAV-21; WP-18).
 *
 *  · A modal dialog opened by the topbar hamburger (« Ouvrir le menu »):
 *    focus trap, scrim, Échap closes and focus goes back to the hamburger.
 *  · The whole tree as an accordion: categories and sub-categories are
 *    expandable buttons, pages are links; the section holding the current
 *    page is open when the drawer opens, and focus lands on the current page.
 *  · Keys: ↑/↓ Début/Fin move between the visible rows, → or Entrée expand,
 *    ← collapses (or goes to the parent row), letters type ahead
 *    (accent-insensitive), Ctrl+Entrée opens a page in a new tab.
 *  · Labels are ALWAYS rendered, whatever « Réduire le menu » says.
 *  · Alt+M opens it on the current page; Alt+1…9 opens it on a category.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Icon, IconButton, Wordmark } from "@/components/ui/primitives";
import { RoleBadge } from "@/components/domain/Domain";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore } from "@/stores/themeStore.js";
import { useNavStore } from "@/stores/navStore.js";
import { useFocusTrap } from "@/hooks/useFocusTrap.js";
import { useShortcuts } from "@/hooks/useShortcuts.js";
import { NAV_TYPEAHEAD_RESET_MS } from "@/config/ui.config.js";
import { moveIndex, typeAhead } from "@/lib/nav-tree.js";
import { NavBadge, NavLock } from "./RailNav";
import { useNavModel, type NavNode } from "./useNavModel";

const isContainer = (n?: NavNode | null) => Array.isArray(n?.children);
const ROW = "[data-nav-row]";

/** Give focus back to the hamburger after an explicit close (§6.4). */
export function focusNavToggle() {
  window.requestAnimationFrame(() => {
    document.querySelector<HTMLElement>("button.pk-mobile-nav-toggle")?.focus();
  });
}

function initialExpanded(nodes: NavNode[], current: Set<string>, extra?: string | null) {
  const out = new Set<string>();
  const walk = (list: NavNode[]) => {
    for (const n of list) {
      if (isContainer(n)) {
        if (current.has(n.id)) out.add(n.id);
        walk(n.children ?? []);
      }
    }
  };
  walk(nodes);
  if (extra) out.add(extra);
  return out;
}

interface DrawerContentProps {
  onClose: () => void;
  focusCategory: string | null;
}

const DrawerContent: React.FC<DrawerContentProps> = ({ onClose, focusCategory }) => {
  const model = useNavModel();
  const navigate = useNavigate();
  const user = useAuthStore((s: any) => s.user);
  const pharmacie = user?.pharmacie ?? null;
  const [expanded, setExpanded] = React.useState(() =>
    initialExpanded(model.nodes, model.current, focusCategory),
  );
  const listRef = React.useRef<HTMLDivElement>(null);
  const initialRef = React.useRef<HTMLElement | null>(null);
  const typed = React.useRef({ text: "", at: 0 });
  const trapRef = useFocusTrap(true, onClose, {
    initialFocusRef: initialRef as React.RefObject<HTMLElement>,
  });

  const toggle = (id: string, open?: boolean) =>
    setExpanded((s) => {
      const next = new Set(s);
      const want = open ?? !next.has(id);
      if (want) next.add(id);
      else next.delete(id);
      return next;
    });

  const rows = () => Array.from(listRef.current?.querySelectorAll<HTMLElement>(ROW) ?? []);
  const focusRow = (el?: HTMLElement | null) => el?.focus();

  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (!target.matches(ROW) || e.altKey || e.metaKey) return;
    const list = rows();
    const i = list.indexOf(target);
    const id = target.dataset.navId ?? "";
    const container = target.dataset.navContainer === "true";
    if (e.ctrlKey) {
      if (e.key === "Enter" && !container && target.getAttribute("href")) {
        e.preventDefault();
        window.open(target.getAttribute("href") as string, "_blank", "noopener");
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Home":
      case "End":
        e.preventDefault();
        focusRow(list[moveIndex(list.length, i, e.key)]);
        return;
      case "ArrowRight":
        if (!container) return;
        e.preventDefault();
        if (!expanded.has(id)) toggle(id, true);
        else focusRow(list[i + 1]);
        return;
      case "ArrowLeft": {
        e.preventDefault();
        if (container && expanded.has(id)) {
          toggle(id, false);
          return;
        }
        const parent = target.dataset.navParent;
        if (parent) focusRow(listRef.current?.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(parent)}"]`));
        return;
      }
      default:
        if (e.key.length === 1 && e.key !== " ") {
          e.preventDefault();
          const now = Date.now();
          typed.current.text =
            now - typed.current.at > NAV_TYPEAHEAD_RESET_MS ? e.key : typed.current.text + e.key;
          typed.current.at = now;
          const j = typeAhead(
            list.map((r) => r.dataset.navLabel ?? r.textContent ?? ""),
            typed.current.text,
            i,
          );
          if (j >= 0) focusRow(list[j]);
        }
    }
  };

  const onLeafClick = (e: React.MouseEvent, to?: string) => {
    if (!to || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };

  const leafId = model.active?.leaf.id;
  const renderNodes = (nodes: NavNode[], depth: number, parentId?: string): React.ReactNode =>
    nodes.map((n) => {
      if (isContainer(n)) {
        const open = expanded.has(n.id);
        const panel = `nav-drawer-${n.id.replace(/\W/g, "-")}`;
        return (
          <li key={n.id} className="pk-nav-drawer-node">
            <button
              type="button"
              data-nav-row
              data-nav-id={n.id}
              data-nav-label={n.label}
              data-nav-container="true"
              data-nav-parent={parentId}
              aria-expanded={open}
              aria-controls={panel}
              aria-current={n.__current ? "true" : undefined}
              className={cn("pk-nav-drawer-row", `pk-nav-drawer-row--d${depth}`, n.__current && "is-current")}
              onClick={() => toggle(n.id)}
              ref={(el) => {
                if (!initialRef.current && focusCategory === n.id) initialRef.current = el;
              }}
            >
              {depth === 0 && n.icon && <Icon name={n.icon} size={22} className="pk-nav-drawer-icon" />}
              <span className="pk-nav-drawer-label">{n.label}</span>
              <NavBadge badgeKey={n.badge} />
              <Icon name={open ? "chevron-down" : "chevron-right"} size={16} className="pk-nav-drawer-chevron" />
            </button>
            {open && (
              <ul id={panel} className="pk-nav-drawer-children" aria-label={n.label}>
                {renderNodes(n.children ?? [], depth + 1, n.id)}
              </ul>
            )}
          </li>
        );
      }
      const isPage = n.id === leafId;
      return (
        <li key={n.id} className="pk-nav-drawer-node">
          <a
            href={n.to}
            data-nav-row
            data-nav-id={n.id}
            data-nav-label={n.label}
            data-nav-parent={parentId}
            aria-current={isPage ? "page" : undefined}
            className={cn("pk-nav-drawer-row", `pk-nav-drawer-row--d${depth}`, isPage && "is-current", n.locked && "is-locked")}
            onClick={(e) => onLeafClick(e, n.to)}
            ref={(el) => {
              if (isPage && !focusCategory) initialRef.current = el;
            }}
          >
            {depth === 0 && n.icon && <Icon name={n.icon} size={22} className="pk-nav-drawer-icon" />}
            <span className="pk-nav-drawer-label">{n.label}</span>
            <NavLock locked={n.locked} />
            <NavBadge badgeKey={n.badge} />
          </a>
        </li>
      );
    });

  const flat = model.mode === "flat";
  return (
    <div
      ref={trapRef as React.RefObject<HTMLDivElement>}
      role="dialog"
      aria-modal="true"
      aria-label="Menu de navigation"
      className="pk-nav-drawer"
    >
      <div className="pk-nav-drawer-head">
        <div className="pk-rail-brand">
          <Wordmark size="md" />
          {pharmacie?.nom && <span className="pk-rail-pharmacy">{pharmacie.nom}</span>}
        </div>
        <IconButton icon="x" label="Fermer le menu" onClick={onClose} data-dialog-close="" />
      </div>
      <nav aria-label="Navigation principale" className="pk-nav-drawer-nav">
        <div ref={listRef} onKeyDown={onKeyDown}>
          {flat ? (
            model.nodes.map((g) => (
              <div key={g.id} className="pk-rail-group">
                <div className="pk-rail-group-heading" id={`nav-drawer-h-${g.id.replace(/\W/g, "-")}`}>
                  {g.label}
                </div>
                <ul aria-labelledby={`nav-drawer-h-${g.id.replace(/\W/g, "-")}`}>
                  {(g.children ?? []).map((n) => {
                    const isPage = n.id === leafId;
                    return (
                      <li key={n.id}>
                        <a
                          href={n.to}
                          data-nav-row
                          data-nav-id={n.id}
                          data-nav-label={n.label}
                          aria-current={isPage ? "page" : undefined}
                          className={cn("pk-nav-drawer-row", "pk-nav-drawer-row--d0", isPage && "is-current")}
                          onClick={(e) => onLeafClick(e, n.to)}
                          ref={(el) => {
                            if (isPage) initialRef.current = el;
                          }}
                        >
                          <Icon name={n.icon ?? ""} size={22} className="pk-nav-drawer-icon" />
                          <span className="pk-nav-drawer-label">{n.label}</span>
                          <NavBadge badgeKey={n.badge} />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          ) : (
            <ul className="pk-nav-drawer-tree">{renderNodes(model.nodes, 0)}</ul>
          )}
        </div>
      </nav>
      {user?.role && (
        <div className="pk-nav-drawer-foot">
          <RoleBadge role={user.role} size="sm" />
        </div>
      )}
    </div>
  );
};

/**
 * Mounted by the shell in drawer mode (< 1024 px): binds Alt+M / Alt+1…9
 * and renders the dialog while the drawer is open.
 */
export const NavDrawerHost: React.FC = () => {
  const open = useThemeStore((s: any) => s.mobileNavOpen);
  const toggleMobileNav = useThemeStore((s: any) => s.toggleMobileNav);
  const closeMobileNav = useThemeStore((s: any) => s.closeMobileNav);
  const flyoutRequest = useNavStore((s: any) => s.flyoutRequest);
  const nodes = useNavModel().nodes;
  const [focusCategory, setFocusCategory] = React.useState<string | null>(null);

  const openOn = React.useCallback(
    (categoryId: string | null) => {
      setFocusCategory(categoryId);
      if (!useThemeStore.getState().mobileNavOpen) toggleMobileNav?.();
    },
    [toggleMobileNav],
  );

  useShortcuts("GLOBAL", {
    MENU: () => openOn(null),
    CATEGORIE: (_e: KeyboardEvent, ctx: { index: number }) => {
      const node = nodes.find((n) => (n as any).hotkey === ctx.index + 1);
      if (!node) return false;
      openOn(isContainer(node) ? node.id : null);
      return true;
    },
  });

  // The breadcrumb's category crumb opens the drawer on that category.
  React.useEffect(() => {
    if (flyoutRequest) openOn(flyoutRequest.categoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyoutRequest?.seq]);

  const close = React.useCallback(() => {
    closeMobileNav?.();
    focusNavToggle();
  }, [closeMobileNav]);

  if (!open) return null;
  return <DrawerContent onClose={close} focusCategory={focusCategory} />;
};

export default NavDrawerHost;
